(function () {
    'use strict';
    angular.module('aprima-core-ui').factory('ClickListenerService', [
        '$parse',
        '$window',
        'DeviceSettings',
        'NgZoneService',
        ClickListenerService
    ]);

    function ClickListenerService(
        $parse,
        $window,
        DeviceSettings,
        NgZoneService
    ) {
        // These changes to wrap the functions using NgZoneService.contextRun() (to runOutsideAngular) are based off of hammer_gestures.ts in Angular
        // where the creation of the Hammer instance, and .on() are wrapped in a similar way.
        // This can also be done carefully in other services, components, etc that get referenced by UpgradeComponents,
        // to fix issues where the the page is really slow because of too much stuff happening inside of the Angular zone,
        // which is then triggering the change detection.
        // This is more important for the hammer events than the click events, so if it's causing issues with the click events, then
        // this service could be changed to just wrap the hammer functions below, like registerHmClick, instead of all 3 of these
        // top-level functions that are returned.

        return {
            parseOptionsFromLink: parseOptionsFromLink,
            register: register,
            registerFromDirective: registerFromDirective,
        };

        function parseOptionsFromLink(scope, element, attrs) {
            return {
                hmOptions: $parse(attrs.hmOptions)(scope, {}),
                hmPropagation: $parse(attrs.hmPropagation)(scope, {}),
                hmStopPropagation: $parse(attrs.hmStopPropagation)(scope, {}),
                hmTapOnly: $parse(attrs.hmTapOnly)(scope, {}),
            };
        }

        function shouldUseHammer() {
            // Hammer doesn't work on some Windows 7/Server 2008 R2 machines,
            // so always fallback to ngClick when in PRM.
            return !DeviceSettings.isPrm &&
                   Hammer !== 'undefined' &&
                   $window.addEventListener;
        }

        function register(element, handlerFn, handlerOpts) {
            var result = NgZoneService.contextRun(function (zoneAwareObject) {
                return zoneAwareRegister(element, handlerFn, handlerOpts, zoneAwareObject);
            });
            return result;
        }

        // This zoneAwareObject always needs to be the last parameter to this function.
        function zoneAwareRegister(element, handlerFn, handlerOpts, zoneAwareObject) {
            var opts = {
                // Enable propagation functionality for this element.
                // Propagation needs to be enabled for the child element
                // to be able to receive the Hammer event
                // when both parent and child have been registered for Hammer events.
                hmPropagation: false,
                // Always call $event.stopPropagation().
                // Automatically turns on Hammer propagation.
                hmStopPropagation: false,
                // If false, treat the tap and press events identically.
                // If true, only listen for tap events using default timings.
                hmTapOnly: false,
            };

            angular.extend(opts, handlerOpts);

            var zoneAwareHandlerFn = zoneAwareObject.wrapToRunInZoneIfNecessary(handlerFn);

            if (shouldUseHammer()) {
                return registerHmClick(element, hmHandler, opts);
            } else {
                return registerNgClick(element, ngHandler);
            }

            function ngHandler(event) {
                zoneAwareHandlerFn(event);

                if (opts.hmStopPropagation) {
                    event.stopPropagation();
                }
            }

            function hmHandler(event) {
                var stopPropagation = event.stopPropagation;
                if(stopPropagation && (opts.hmPropagation || opts.hmStopPropagation)) {
                    event.stopPropagation = function() {
                        stopSrcPropagation(event);
                        stopPropagation.apply(event, arguments);
                    };
                }

                if (!event.isDefaultPrevented) {
                    event.isDefaultPrevented = function clsIsDefaultPrevented() {
                        return event.srcEvent && event.srcEvent.defaultPrevented;
                    };
                }

                ngHandler(event);
            }
        }

        function stopSrcPropagation(hammerEv) {
            var srcEvent = hammerEv && hammerEv.srcEvent;
            if(!(srcEvent instanceof Event))  {
                return;
            }

            // propagating-hammerjs redefines stopPropagation, so make sure we are calling the prototype one
            Event.prototype.stopPropagation.call(srcEvent);
        }

        function registerFromDirective(scope, element, attrs, attributeName) {
            var result = NgZoneService.contextRun(function (zoneAwareObject) {
                return zoneAwareRegisterFromDirective(scope, element, attrs, attributeName, zoneAwareObject);
            });
            return result;
        }

        // This zoneAwareObject always needs to be the last parameter to this function.
        function zoneAwareRegisterFromDirective(scope, element, attrs, attributeName, zoneAwareObject) {
            var registerOpts = parseOptionsFromLink(scope, element, attrs);
            // calling $parse with 3 arguments, rather than just the expression comes from ngclick in 1.5.x
            var parsedFn = $parse(attrs[attributeName], null, true);

            var deregister = zoneAwareRegister(element, clickHandler, registerOpts, zoneAwareObject);

            scope.$on('$destroy', deregister);

            function clickHandler(event) {
                // Previously (look at comment below), this was not wrapped in NgZoneService.run because it didn't need to manually trigger a run of Angular change detection,
                // but the reason I'm realizing that it needs to now is that this callback could be calling back into Angular code (which means it needs to be in the Ng Zone).
                // And I would rather handle fixing that here, than having to know in specific places if we need to run certain Angular code inside of the Ng Zone.
                // This situation would happen either directly or indirectly from an UpgradeComponent or upgraded injectable.
                // This handler gets wrapped to run inside of the Ng Zone above, when it is passed into register().
                // =================================================================================================================
                // Previous logic:
                // We currently don't need to run this inside of NgZone in hybrid mode,
                // because it will detect changes from this digest cycle started by scope.$apply.
                // The reason is because of the ForceLoadAngularComponent, which is downgraded with propagateDigest = true,
                // which causes the Angular change detection to run on every $digest.
                // If that changes, then code that is doing scope.$apply outside of Angular, like this line will need to be wrapped in NgZoneService.run().
                // For reference: https://angular.io/api/upgrade/static/downgradeComponent#downgradecomponent
                // =================================================================================================================
                scope.$apply(callback);

                function callback() {
                    parsedFn(scope, { $event: event });
                }                
            }
        }

        /* Adapted from:
        * angular-hammer v1.2.4
        * (c) 2013 Monospaced http://monospaced.com
        * License: MIT
        */
        /*  This fixes an issue with hm-tap where longer taps do not register as taps but as presses.
            Hammerjs requires a maximum tap time of 250ms which seemed to be too short when actually using devices.

            This change makes hm-click fire for taps AND presses.

            hm-click should NOT be used in cases where a press and tap are expected to be handled differently
        */
        function registerHmClick(element, handlerFn, opts) {
            // don't create multiple Hammer instances per element
            var hammer = element.data('hammer');
            var shouldDestroy = false;
            if (!hammer) {
                hammer = new Hammer.Manager(element[0], opts.hmOptions);

                // we are creating a new instance in this function,
                // so it needs to be cleaned up in here as well, below in the returned dereg function
                shouldDestroy = true;

                if (opts.hmPropagation || opts.hmStopPropagation) {
                    hammer = propagating(hammer);
                }

                element.data('hammer', hammer);
            }

            // Make sure that a click event is ignored when it was already handled as a tap or press.
            $window.PreventGhostClick(element[0]);

            // bind Hammer touch event
            if (opts.hmTapOnly) {
                hammer.add(new Hammer.Tap());

                hammer.on('tap', handlerFn);
            } else {
                hammer.add(new Hammer.Tap({ time: 499 }));
                hammer.add(new Hammer.Press({ time: 500 }));

                hammer.on('tap', handlerFn);
                hammer.on('pressup', handlerFn);
            }

            return function hmCleanup() {
                // unbind Hammer touch event
                hammer.off('tap', handlerFn);

                if (!opts.hmTapOnly) {
                    hammer.off('pressup', handlerFn);
                }

                if (shouldDestroy) {
                    hammer.destroy();
                }
            };
        }

        function registerNgClick(element, handlerFn) {
            element.on('click', handlerFn);

            return function ngCleanup() {
                element.off('click', handlerFn);
            };
        }
    }
})();